import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { MdOutlineEventAvailable } from "react-icons/md";
import { twMerge } from "tailwind-merge";

import { CustomButton } from "../../../atoms/CustomButton";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { EmptyState } from "../../../atoms/EmptyState";
import { Spinner } from "../../../atoms/Spinner";
import NoTranslate from "../../../atoms/NoTranslate";
import type { AgendaMember } from "../../../../hooks/agenda";
import type { BookingSlot } from "../../../../hooks/agenda/publicBooking";
import { dayLabel, rangeLabel } from "../../dashboard/agenda/agendaDates";
import { momentLocaleOf } from "../../../../utils/momentLocale";

const DAYS_PER_PAGE = 7;
const DAY_KEY = "YYYY-MM-DD";

export const useBookingMoment = () => {
  const { i18n } = useTranslation("clientView");
  const lang = (i18n.language || "en").split("-")[0];
  const momentLocale = momentLocaleOf(lang);
  const at = (ms: number) => moment(ms).locale(momentLocale);
  return { lang, momentLocale, at };
};

export const optionClass = (selected: boolean) =>
  twMerge(
    "rounded-md border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50",
    selected
      ? "border-blue-600 bg-blue-50 font-medium text-blue-700"
      : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"
  );

interface BookingSlotPickerProps {
  members: AgendaMember[];
  member?: string;
  onMemberChange: (id: string) => void;
  showMemberChoice: boolean;
  slots: BookingSlot[];
  isLoadingSlots: boolean;
  isSlotsError: boolean;
  start?: number;
  onStartChange: (start: number | undefined) => void;
  onConfirm: (slot: BookingSlot) => void;
  isConfirming: boolean;
  /** No time can be booked any more, e.g. once the client has reached their limit of appointments. */
  slotsDisabled?: boolean;
  /** Above the confirmation button, e.g. the price. */
  confirmNote?: React.ReactNode;
  notice?: React.ReactNode;
  memberName: (id?: string) => string;
}

export const BookingSlotPicker: React.FC<BookingSlotPickerProps> = ({
  members,
  member,
  onMemberChange,
  showMemberChoice,
  slots,
  isLoadingSlots,
  isSlotsError,
  start,
  onStartChange,
  onConfirm,
  isConfirming,
  slotsDisabled = false,
  confirmNote,
  notice,
  memberName,
}) => {
  const { t } = useTranslation("clientView");
  const { momentLocale, at } = useBookingMoment();
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const [dayKey, setDayKey] = useState<string | undefined>();
  const [page, setPage] = useState(0);

  const slotsByDay = useMemo(() => {
    const groups = new Map<string, BookingSlot[]>();
    [...slots]
      .sort((a, b) => a.start - b.start)
      .forEach((slot) => {
        const key = moment(slot.start).locale("en").format(DAY_KEY);
        groups.set(key, [...(groups.get(key) ?? []), slot]);
      });
    return groups;
  }, [slots]);

  const days = useMemo(() => Array.from(slotsByDay.keys()), [slotsByDay]);
  const pageCount = Math.max(1, Math.ceil(days.length / DAYS_PER_PAGE));
  const visibleDays = days.slice(
    page * DAYS_PER_PAGE,
    (page + 1) * DAYS_PER_PAGE
  );
  const startDay =
    start !== undefined
      ? moment(start).locale("en").format(DAY_KEY)
      : undefined;
  const activeDay =
    dayKey && slotsByDay.has(dayKey)
      ? dayKey
      : startDay && slotsByDay.has(startDay)
      ? startDay
      : days[0];
  const daySlots = activeDay ? slotsByDay.get(activeDay) ?? [] : [];
  const selectedSlot = daySlots.find((slot) => slot.start === start);

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  // A time picked before leaving the page shows on its own day once the slots are back.
  useEffect(() => {
    if (dayKey || !startDay) return;
    const index = days.indexOf(startDay);
    if (index >= 0) setPage(Math.floor(index / DAYS_PER_PAGE));
  }, [dayKey, startDay, days]);

  const chooseMember = (id: string) => {
    setDayKey(undefined);
    setPage(0);
    onMemberChange(id);
  };

  const chooseDay = (key: string) => {
    setDayKey(key);
    onStartChange(undefined);
  };

  const dayOf = (key: string) =>
    moment(key, DAY_KEY, "en").locale(momentLocale);

  return (
    <>
      {notice}

      {showMemberChoice && members.length > 1 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-gray-900">
            {t("customRegistration.page.booking.chooseMember")}
          </h3>
          <div className="flex flex-wrap gap-2">
            {members.map((m) => (
              <button
                key={m.user_identification}
                type="button"
                aria-pressed={member === m.user_identification}
                onClick={() => chooseMember(m.user_identification)}
                className={optionClass(member === m.user_identification)}
              >
                <NoTranslate>{memberName(m.user_identification)}</NoTranslate>
              </button>
            ))}
          </div>
        </section>
      )}

      {member && isLoadingSlots && (
        <div className="flex justify-center py-8" translate="no">
          <Spinner size="md" />
        </div>
      )}

      {member && isSlotsError && (
        <AlertMessage color="failure">
          {t("customRegistration.page.booking.loadError")}
        </AlertMessage>
      )}

      {member && !isLoadingSlots && !isSlotsError && days.length === 0 && (
        <EmptyState
          bordered
          icon={<MdOutlineEventAvailable size={24} />}
          title={t("customRegistration.page.booking.noSlotsTitle")}
          description={t(
            showMemberChoice && members.length > 1
              ? "customRegistration.page.booking.noSlotsOtherMember"
              : "customRegistration.page.booking.noSlotsDescription"
          )}
        />
      )}

      {member && days.length > 0 && (
        <>
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-gray-900">
                {t("customRegistration.page.booking.chooseDay")}
              </h3>
              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <CustomButton
                    variant="secondary"
                    size="icon"
                    icon={
                      <HiChevronLeft size={16} className="rtl:rotate-180" />
                    }
                    tooltip={
                      t(
                        "customRegistration.page.booking.previousDays"
                      ) as string
                    }
                    aria-label={
                      t(
                        "customRegistration.page.booking.previousDays"
                      ) as string
                    }
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  />
                  <CustomButton
                    variant="secondary"
                    size="icon"
                    icon={
                      <HiChevronRight size={16} className="rtl:rotate-180" />
                    }
                    tooltip={
                      t("customRegistration.page.booking.nextDays") as string
                    }
                    aria-label={
                      t("customRegistration.page.booking.nextDays") as string
                    }
                    disabled={page >= pageCount - 1}
                    onClick={() =>
                      setPage((p) => Math.min(pageCount - 1, p + 1))
                    }
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {visibleDays.map((key, index) => {
                const day = dayOf(key);
                const selected = key === activeDay;
                const startsMonth =
                  index === 0 ||
                  !day.isSame(dayOf(visibleDays[index - 1]), "month");
                const secondary = twMerge(
                  "w-full truncate text-xs",
                  !selected && "text-gray-500"
                );
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={selected}
                    aria-label={dayLabel(day)}
                    onClick={() => chooseDay(key)}
                    className={twMerge(
                      optionClass(selected),
                      "flex min-w-0 flex-col items-center px-0 text-center"
                    )}
                  >
                    <span className={secondary}>
                      {day.localeData().weekdaysShort(day)}
                    </span>
                    <span className="text-base font-semibold tabular-nums">
                      {day.format("D")}
                    </span>
                    {startsMonth && (
                      <span className={secondary}>
                        {day.localeData().monthsShort(day)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {activeDay && (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900">
                {dayLabel(dayOf(activeDay))}
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {daySlots.map((slot) => (
                  <button
                    key={slot.start}
                    type="button"
                    aria-pressed={slot.start === start}
                    disabled={slotsDisabled}
                    onClick={() => onStartChange(slot.start)}
                    className={twMerge(
                      optionClass(slot.start === start),
                      "tabular-nums"
                    )}
                  >
                    {at(slot.start).format("LT")}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {t("customRegistration.page.booking.timezoneHint", {
                  timezone,
                })}
              </p>
            </section>
          )}

          {confirmNote}
          <CustomButton
            variant="primary"
            title={
              selectedSlot
                ? (t("customRegistration.page.booking.confirmSlot", {
                    time: rangeLabel(
                      t,
                      "time",
                      at(selectedSlot.start).format("LT"),
                      at(selectedSlot.end).format("LT")
                    ),
                  }) as string)
                : (t("customRegistration.page.booking.pickSlot") as string)
            }
            className="w-full"
            onClick={() => selectedSlot && onConfirm(selectedSlot)}
            isLoading={isConfirming}
            disabled={!selectedSlot || isConfirming || slotsDisabled}
          />
        </>
      )}
    </>
  );
};

export default BookingSlotPicker;
